import React, { useState } from "react";
import { Eye, EyeOff, FileSpreadsheet, FileText, Pencil, Trash2 } from "lucide-react";
import { istDate } from "@/components/DispatchDatePicker";
import { TYPE_LABEL, obDir } from "@/lib/payoutExport";

export const inr = (n) => (n < 0 ? "-₹" : "₹") + Math.abs(Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const neg = (n) => (n < 0 ? "text-[#C8553D]" : "");
export const fmtD = (ymd) =>
  ymd ? new Date(`${ymd}T12:00:00+05:30`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const monthStart = (ymd) => `${ymd.slice(0, 8)}01`;
const prevMonth = () => {
  const end = new Date(`${monthStart(istDate(0))}T12:00:00+05:30`);
  end.setDate(0);
  const to = end.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  return { from: monthStart(to), to };
};
export const PRESETS = {
  today: () => ({ from: istDate(0), to: istDate(0) }),
  yesterday: () => ({ from: istDate(1), to: istDate(1) }),
  month: () => ({ from: monthStart(istDate(0)), to: istDate(0) }),
  lastmonth: prevMonth,
  all: () => ({ from: "", to: istDate(0) }),
};
const PRESET_LABEL = { today: "Today", yesterday: "Yesterday", month: "This Month", lastmonth: "Last Month", all: "All Time", custom: "Custom" };
export const defaultRange = () => ({ preset: "month", ...PRESETS.month() });

export function RangeBar({ range, setRange }) {
  const pick = (k) => setRange(k === "custom" ? { ...range, preset: k } : { preset: k, ...PRESETS[k]() });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="payout-range">
      {Object.keys(PRESET_LABEL).map((k) => (
        <button key={k} onClick={() => pick(k)} data-testid={`payout-range-${k}`}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${range.preset === k ? "bg-[#385C4B] border-[#385C4B] text-white" : "border-[#DCDCD3] text-[#3D3D36] hover:bg-[#F0F0EA]"}`}>
          {PRESET_LABEL[k]}
        </button>
      ))}
      {range.preset === "custom" && (
        <span className="flex items-center gap-1 text-xs">
          <input type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })}
            className="border border-[#DCDCD3] rounded px-2 py-1" data-testid="payout-range-from" />
          <span>to</span>
          <input type="date" value={range.to} max={istDate(0)} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })}
            className="border border-[#DCDCD3] rounded px-2 py-1" data-testid="payout-range-to" />
        </span>
      )}
    </div>
  );
}

const CARD = [["earned", "Total Earned", "text-[#385C4B]"], ["plus", "Bonus / Extra", "text-[#4D7C0F]"], ["paid", "Total Paid", "text-[#2F6F8F]"], ["minus", "Deductions", "text-[#C8553D]"], ["balance", "Balance", "text-[#1C1C19]"]];
export function Cards({ s, showOpening }) {
  const list = showOpening ? [["opening", s?.opening_kind === "carry" ? "Brought Forward" : "Opening", "text-[#B7791F]"], ...CARD] : CARD;
  return (
    <div className={`grid grid-cols-2 ${showOpening ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3`}>
      {list.map(([k, label, cls]) => (
        <div key={k} className="bg-white border border-[#EDEDE5] rounded-lg p-3" data-testid={`payout-card-${k}`}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">{label}</div>
          <div className={`text-xl font-semibold ${(k === "balance" || k === "opening") && s?.[k] < 0 ? "text-[#C8553D]" : cls}`} style={{ fontFamily: "Outfit, sans-serif" }}>{inr(s?.[k])}</div>
        </div>
      ))}
    </div>
  );
}

export function ExportButtons({ onExcel, onPdf, prefix }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const cls = "text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-[#DCDCD3] hover:bg-[#F0F0EA] disabled:opacity-50";
  return (
    <div className="flex gap-2">
      <button disabled={busy} onClick={() => run(onExcel)} className={cls} data-testid={`${prefix}-excel`}><FileSpreadsheet className="w-3.5 h-3.5 text-[#385C4B]" />Excel</button>
      <button disabled={busy} onClick={() => run(onPdf)} className={cls} data-testid={`${prefix}-pdf`}><FileText className="w-3.5 h-3.5 text-[#C8553D]" />PDF</button>
    </div>
  );
}

function OpeningRow({ st, onEditOpening, onDeleteOpening }) {
  const ob = st.opening_balance;
  const isOb = st.opening_kind === "opening";
  return (
    <tr className={`border-t border-[#F0F0EA] ${isOb ? "bg-[#FFF8E6]" : "text-[#6B6B61]"}`} data-testid="payout-opening-row">
      <td className="px-3 py-1.5" colSpan={9}>
        <span className={isOb ? "font-semibold" : ""} data-testid="payout-opening-label">{isOb ? `Opening balance · from ${fmtD(ob.date)}` : st.opening_kind === "carry" ? "Brought forward (before this period)" : "Opening balance (not set)"}</span>
        {isOb && <span className="ml-2 text-xs text-[#6B6B61]">{obDir(ob.amount)}{ob.note ? ` · ${ob.note}` : ""}</span>}
      </td>
      <td className={`px-3 py-1.5 font-semibold whitespace-nowrap ${neg(st.opening)}`} data-testid="payout-opening-amount">{inr(st.opening)}</td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        {isOb && onEditOpening && (
          <>
            <button onClick={onEditOpening} className="p-1 rounded hover:bg-[#F0F0EA]" title="Edit" data-testid="payout-opening-edit"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={onDeleteOpening} className="p-1 rounded hover:bg-red-50 text-[#C8553D]" title="Delete" data-testid="payout-opening-delete"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        )}
      </td>
    </tr>
  );
}

const TYPE_CLS = { work: "bg-[#385C4B]/10 text-[#385C4B]", plus: "bg-[#4D7C0F]/10 text-[#4D7C0F]", payment: "bg-[#2F6F8F]/10 text-[#2F6F8F]", minus: "bg-[#C8553D]/10 text-[#C8553D]" };
export function StatementTable({ st, onEdit, onDelete, onEditOpening, onDeleteOpening }) {
  const [showDel, setShowDel] = useState(false);
  const editable = (r) => showDel && !!r.id;
  const DelIcon = showDel ? EyeOff : Eye;
  return (
    <div className="space-y-2">
      {onEdit && (
        <div className="flex justify-end">
          <button onClick={() => setShowDel(!showDel)} data-testid="payout-toggle-delete"
            className={`text-[11px] font-mono uppercase tracking-wider flex items-center gap-1.5 px-3 py-1.5 rounded border transition-colors ${showDel ? "border-[#C8553D] text-[#C8553D] bg-red-50" : "border-[#DCDCD3] text-[#6B6B61] hover:bg-[#F0F0EA]"}`}>
            <DelIcon className="w-3.5 h-3.5" />{showDel ? "Hide Delete" : "Show Delete"}
          </button>
        </div>
      )}
    <div className="overflow-x-auto border border-[#EDEDE5] rounded-lg bg-white">
      <table className="w-full text-sm" data-testid="payout-statement">
        <thead className="bg-[#F5F5F0] text-[10px] font-mono uppercase text-[#6B6B61]">
          <tr>{["Date", "Type", "Qty", "Rate", "Earned", "Bonus", "Paid", "Deduction", "Note", "Balance", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
        </thead>
        <tbody>
          <OpeningRow st={st} onEditOpening={showDel ? onEditOpening : null} onDeleteOpening={onDeleteOpening} />
          {!st.rows.length && <tr><td colSpan={11} className="text-sm text-[#6B6B61] py-6 text-center" data-testid="payout-statement-empty">No entries in this period.</td></tr>}
          {st.rows.map((r, i) => (
            <tr key={r.id || `${r.date}-${i}`} className="border-t border-[#F0F0EA]" data-testid={`payout-row-${i}`}>
              <td className="px-3 py-1.5 whitespace-nowrap">{fmtD(r.date)}</td>
              <td className="px-3 py-1.5"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${TYPE_CLS[r.type]}`}>{TYPE_LABEL[r.type]}</span></td>
              <td className="px-3 py-1.5">{r.type === "work" ? r.qty : ""}</td>
              <td className="px-3 py-1.5 text-xs font-mono">{r.type === "work" ? r.rate_label : ""}</td>
              <td className="px-3 py-1.5">{r.type === "work" ? inr(r.amount) : ""}</td>
              <td className="px-3 py-1.5">{r.type === "plus" ? inr(r.amount) : ""}</td>
              <td className="px-3 py-1.5">{r.type === "payment" ? inr(r.amount) : ""}</td>
              <td className="px-3 py-1.5">{r.type === "minus" ? inr(r.amount) : ""}</td>
              <td className="px-3 py-1.5 text-xs text-[#6B6B61]">{[r.mode, r.note].filter(Boolean).join(" · ")}</td>
              <td className={`px-3 py-1.5 font-semibold whitespace-nowrap ${neg(r.balance)}`}>{inr(r.balance)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">
                {onEdit && editable(r) && (
                  <>
                    <button onClick={() => onEdit(r)} className="p-1 rounded hover:bg-[#F0F0EA]" title="Edit" data-testid={`payout-row-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDelete(r)} className="p-1 rounded hover:bg-red-50 text-[#C8553D]" title="Delete" data-testid={`payout-row-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}
